import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, clinicApi } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

export interface AptCounterStaffState {
  clinicId: string;
  currentNumber: number;
  doctorIn: boolean;
  doctorName: string | null;
  updatedAt: string;
}

export const APT_COUNTER_KEY = ['apt-counter'] as const;

export function useAptCounter() {
  const { isAuthenticated, activeClinic } = useAuth();
  const enabled = isAuthenticated && !!activeClinic;
  return useQuery({
    queryKey: [...APT_COUNTER_KEY, activeClinic?.id ?? null],
    queryFn: () => clinicApi.get<AptCounterStaffState>('apt-counter'),
    enabled,
    staleTime: 5_000,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: APT_COUNTER_KEY });
}

export function useSetCounterNumber() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (value: number) =>
      clinicApi.post<AptCounterStaffState>('apt-counter/number', { value }),
    onSuccess: invalidate,
  });
}

export function useIncrementCounter() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => clinicApi.post<AptCounterStaffState>('apt-counter/increment'),
    onSuccess: invalidate,
  });
}

export function useDecrementCounter() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: () => clinicApi.post<AptCounterStaffState>('apt-counter/decrement'),
    onSuccess: invalidate,
  });
}

export interface SetDoctorPayload {
  doctorIn: boolean;
  doctorName?: string;
}

export function useSetDoctor() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (payload: SetDoctorPayload) =>
      clinicApi.post<AptCounterStaffState>('apt-counter/doctor', payload),
    onSuccess: invalidate,
  });
}
